import React, { useState } from 'react';
import { useActor } from '@xstate/react';
import { FormStateBasic } from 'informed';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { AuthCoreMachineRefType } from '@nw/auth-core';
import { InformedCheckbox } from '@nw/design-system/omega';
import { DialCodes, otpFieldConstants } from '@nw/shared/config';
import { BackArrowIcon, OTPFieldWithResend } from '@nw/shared/ui';
import { isValidSixDigitOTP } from '@nw/shared/utils';

import { userAuthHandlerMachineEvents } from '../../constants/userAuthHandlerMachineConstants';
import {
  OnClickBackButton,
  UserAuthHandlerMachineSendType,
  UserAuthHandlerMachineStateType
} from '../../types/UserAuthHandlerMachineTypes';

import {
  AuthFormContainerHeading,
  ErrorText
} from '../AuthPhoneNumberInputCard/styledComponents';
import { CantLoginModal } from '../CantLoginModal';
import VerifyOtpButton from '../VerifyOtpButton';

import {
  AuthOtpInputCardContainer,
  AuthOtpInputCardHeaderContainer,
  AuthOtpInputCardHeaderDescription,
  BackButtonContainer,
  CantLogin,
  CantLoginButton,
  CheckBoxLabelText,
  OTPBoxesFieldContainerStyles,
  PhoneNumberText,
  Policies
} from './styledComponent';

interface AuthOtpInputCardProps {
  validationSchemaObject: Yup.AnyObjectSchema;

  formState: FormStateBasic;
  authMachineRef: AuthCoreMachineRefType;
  troubleLoggingIFrameURL?: string;
  userAuthHandlerMachineSend: UserAuthHandlerMachineSendType;
  userAuthHandlerMachineState: UserAuthHandlerMachineStateType;
}

const AuthOtpInputCard: React.FC<AuthOtpInputCardProps> = ({
  validationSchemaObject,
  formState,
  authMachineRef,
  troubleLoggingIFrameURL,
  userAuthHandlerMachineSend,
  userAuthHandlerMachineState
}) => {
  const { t } = useTranslation();
  const otpField = 'otp';
  const checkBoxField = 'signUpTermsAndConditionsCheckbox';
  const { values, errors, touched } = formState;

  const {
    isNewUser,
    isAuthButtonLoading,
    errorMessage,
    values: contextValues
  } = userAuthHandlerMachineState.context;

  const [authCoreMachineState, authCoreMachineSend] = useActor(authMachineRef);

  const { otpFieldRef } = authCoreMachineState.context;

  const [isCantLoginModalOpen, setIsCantLoginModalOpen] = useState(false);

  const onCloseCantLoginModal = () => {
    setIsCantLoginModalOpen(false);
  };

  const onClickCantLogin = () => {
    setIsCantLoginModalOpen(true);
  };

  const renderCantLoginButton = () => {
    const id = isNewUser
      ? 'trouble-logging-in-signup'
      : 'trouble-logging-in-login';

    return (
      <CantLoginButton
        onClick={onClickCantLogin}
        disabled={isAuthButtonLoading}
        id={id}
        type={'button'}
      >
        <CantLogin>{t('common.cantLogin')}</CantLogin>
      </CantLoginButton>
    );
  };

  const renderCantLoginModal = () => {
    return (
      <CantLoginModal
        isOpen={isCantLoginModalOpen}
        onCloseCantLogin={onCloseCantLoginModal}
        iframeURL={troubleLoggingIFrameURL}
      />
    );
  };

  const renderAuthOtpInputField = () => {
    return (
      <OTPFieldWithResend
        field={otpField}
        label={t('login.oneTimePasscode')}
        validationSchemaObject={validationSchemaObject}
        state={authCoreMachineState}
        formState={formState}
        resendOtpButtonWithTimerVariant={
          otpFieldConstants.timerAwayFromResendOtp
        }
        labelStyles={'authOtpInputFieldLabelStyles'}
        errorMessageStyles={'authOtpInputFieldErrorMessageStyles'}
        OTPBoxesFieldContainerStyles={OTPBoxesFieldContainerStyles}
        otpContainerStyles={'authOtpFieldContainerStyles'}
        renderAgreeTermsAndConditionsCheckbox={
          renderAgreeTermsAndConditionsCheckbox
        }
      />
    );
  };

  const renderAgreeTermsAndConditionsCheckbox = () => {
    if (!isNewUser) return null;
    return (
      <InformedCheckbox
        field={checkBoxField}
        key={checkBoxField}
        validationSchemaObject={validationSchemaObject}
        error={errors[checkBoxField]}
        touched={touched[checkBoxField]}
        isInnerHTML={true}
        isMarginBottomZero={true}
        shouldShowOnlyLabel={true}
      >
        <CheckBoxLabelText className="new-user-login-policies-text">
          {t('common.checkbox.agreeContent')}{' '}
          <Policies
            referrerPolicy="no-referrer-when-downgrade"
            href="https://ccbp.in/terms-and-conditions"
            target="blank"
            className="new-user-login-policies-link"
          >
            {t('common.checkbox.terms&Conditions')}
          </Policies>{' '}
          {t('common.checkbox.and')}{' '}
          <Policies
            referrerPolicy="no-referrer-when-downgrade"
            href="https://ccbp.in/privacy-policy"
            target="blank"
          >
            {t('common.checkbox.privacyPolicy')}
          </Policies>
        </CheckBoxLabelText>
      </InformedCheckbox>
    );
  };

  const onClickBackButton = () => {
    userAuthHandlerMachineSend({
      type: userAuthHandlerMachineEvents.onClickBackButton
    } as OnClickBackButton);
  };

  const renderAuthOtpInputHeader = () => {
    return (
      <>
        <AuthOtpInputCardHeaderContainer
          className={'authOtpInputCardHeaderContainerClassName'}
        >
          <BackButtonContainer
            className={'authOtpInputCardHeaderBackButton'}
            onClick={onClickBackButton}
          >
            <BackArrowIcon />
          </BackButtonContainer>
          <AuthFormContainerHeading className={'authOtpInputCardHeaderText'}>
            {isNewUser ? t('common.signUp') : t('common.login')}
          </AuthFormContainerHeading>
        </AuthOtpInputCardHeaderContainer>
        <AuthOtpInputCardHeaderDescription
          className={'authOtpInputCardHeaderDescription'}
        >
          {t('login.weHaveSentVerificationCodetoYourMobileNumber')}
          <PhoneNumberText>
            {` ${DialCodes.india}`}&nbsp;{contextValues['phoneNumber']}
          </PhoneNumberText>
        </AuthOtpInputCardHeaderDescription>
      </>
    );
  };

  const isVerifyAndSignUpButtonDisabled = (): boolean => {
    if (isNewUser) {
      const isCheckBoxChecked = values[checkBoxField] as boolean;
      return !isValidSixDigitOTP(values['otp'] as string) || !isCheckBoxChecked;
    } else return !isValidSixDigitOTP(values['otp'] as string);
  };

  const renderVerifyOtpButton = () => {
    if (!otpFieldRef) return null;
    return (
      <VerifyOtpButton
        otpFieldRef={otpFieldRef}
        isVerifyAndSignUpButtonDisabled={isVerifyAndSignUpButtonDisabled()}
        otp={values['otp'] as string}
        buttonId={'verifyOtpButton'}
        buttonText={t('common.verifyAndLogin')}
        isButtonLoading={isAuthButtonLoading}
        userAuthHandlerMachineSend={userAuthHandlerMachineSend}
        userAuthHandlerMachineState={userAuthHandlerMachineState}
      />
    );
  };
  const renderErrorMessage = () => {
    return (
      <ErrorText className="login-error-message-text">{errorMessage}</ErrorText>
    );
  };

  return (
    <AuthOtpInputCardContainer className={'auth-otp-input-card-container'}>
      {renderAuthOtpInputHeader()}
      {renderAuthOtpInputField()}
      {renderVerifyOtpButton()}
      {renderErrorMessage()}
      {renderCantLoginButton()}
      {renderCantLoginModal()}
    </AuthOtpInputCardContainer>
  );
};

export { AuthOtpInputCard };
